import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type RefObject } from 'react';

export const MEDIA = {
  phone: '(max-width: 767px)',
  belowDesktop: '(max-width: 1023px)',
  rail: '(min-width: 1024px) and (max-width: 1279px)',
  desktop: '(min-width: 1024px)',
  wide: '(min-width: 1280px)',
  coarse: '(pointer: coarse)',
} as const;

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (cb: () => void) => {
      const mql = matchMedia(query);
      mql.addEventListener('change', cb);
      return () => mql.removeEventListener('change', cb);
    },
    [query],
  );
  return useSyncExternalStore(subscribe, () => matchMedia(query).matches, () => false);
}

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writeStorage(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota/private-mode errors
  }
}

/** State persisted to localStorage (JSON). */
export function useLocalStorageState<T>(key: string, initial: T): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readStorage(key, initial));
  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        writeStorage(key, resolved);
        return resolved;
      });
    },
    [key],
  );
  return [value, set];
}

/** Keeps a ref pointing at the latest value (for stable callbacks). */
export function useLatest<T>(value: T): RefObject<T> {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

export function useDocumentTitle(title: string | undefined): void {
  useEffect(() => {
    document.title = title ? `${title} · NATASHU` : 'NATASHU Chat & CRM';
  }, [title]);
}

// ---------------------------------------------------------------------------
// Layered overlays: only the top-most modal/drawer/menu reacts to Esc and traps focus.

const layerStack: symbol[] = [];

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

export function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getClientRects().length > 0,
  );
}

export interface FocusTrapOptions {
  onEscape?: () => void;
  initialFocus?: RefObject<HTMLElement | null>;
  restoreFocus?: boolean;
  /** Trap Tab inside the container (modals). Menus only need Esc + restore. */
  trapTab?: boolean;
}

export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean, options: FocusTrapOptions = {}): void {
  const opts = useLatest(options);
  useEffect(() => {
    if (!active) return;
    const layer = Symbol('layer');
    layerStack.push(layer);
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const frame = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) return;
      if (container.contains(document.activeElement)) return;
      const target = opts.current.initialFocus?.current ?? getFocusable(container)[0] ?? container;
      target.focus({ preventScroll: true });
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (layerStack[layerStack.length - 1] !== layer) return;
      const container = containerRef.current;
      if (!container) return;
      if (event.key === 'Escape' && opts.current.onEscape) {
        event.preventDefault();
        event.stopPropagation();
        opts.current.onEscape();
        return;
      }
      if (event.key !== 'Tab' || opts.current.trapTab === false) return;
      const items = getFocusable(container);
      if (items.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !container.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !container.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown, true);
      const idx = layerStack.indexOf(layer);
      if (idx >= 0) layerStack.splice(idx, 1);
      if (opts.current.restoreFocus !== false && previouslyFocused?.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [active, containerRef, opts]);
}

let scrollLocks = 0;
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const root = document.documentElement;
    if (scrollLocks === 0) {
      const scrollbar = window.innerWidth - root.clientWidth;
      root.style.overflow = 'hidden';
      if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
    }
    scrollLocks += 1;
    return () => {
      scrollLocks -= 1;
      if (scrollLocks === 0) {
        root.style.overflow = '';
        root.style.paddingRight = '';
      }
    };
  }, [active]);
}

export function useOnClickOutside(refs: ReadonlyArray<RefObject<HTMLElement | null>>, handler: () => void, active: boolean): void {
  const latest = useLatest(handler);
  useEffect(() => {
    if (!active) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (refs.some((r) => r.current?.contains(target))) return;
      latest.current();
    };
    document.addEventListener('pointerdown', onPointer, true);
    return () => document.removeEventListener('pointerdown', onPointer, true);
    // `refs` holds stable RefObjects; re-subscribing when the array identity changes is unnecessary.
  }, [active]);
}
